import React, { useCallback, useEffect, useState } from "react"
import { api } from "../api/client"
import { useLanguage } from "../context/LanguageContext"
import { sentenceAround } from "../utils/sentenceAround"

export default function ExerciseVocabulary({
  skill,
  lessonId,
  lessonTitle,
  sourceRef,
  selectedText,
  onClearSelection,
  onWordsChange,
  contextText,
  suggestions = [],
}) {
  const { t } = useLanguage()
  const [words, setWords] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")

  const load = useCallback(async () => {
    if (!lessonId) return
    try {
      const data = await api.listVocabulary({ lessonId })
      setWords(Array.isArray(data) ? data : [])
    } catch {
      /* keep current list */
    }
  }, [lessonId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (onWordsChange) onWordsChange(words)
  }, [words, onWordsChange])

  const save = async ({ word, meaning, example }) => {
    const text = (word || "").trim()
    if (!text) return
    setSaving(true)
    setError("")
    setNotice("")
    try {
      await api.saveVocabulary({
        word: text,
        meaning: meaning || undefined,
        example_sentence: example || sentenceAround(contextText, text) || undefined,
        skill,
        lesson_id: lessonId || undefined,
        lesson_title: lessonTitle || undefined,
        source_ref: sourceRef || undefined,
        context_snippet: sentenceAround(contextText, text) || undefined,
      })
      setNotice(t("vocabSaved"))
      if (onClearSelection) onClearSelection()
      await load()
    } catch (exc) {
      setError(exc.message || t("vocabSaveFailed"))
    } finally {
      setSaving(false)
    }
  }

  const savedWords = new Set(words.map((item) => item.word))
  const openSuggestions = suggestions.filter(
    (item) => item.word && !savedWords.has(String(item.word).toLowerCase())
  )

  if (!lessonId && !selectedText && openSuggestions.length === 0 && words.length === 0) {
    return null
  }

  return (
    <section className="vocab-inline" aria-label={t("vocabulary")}>
      <div className="vocab-inline-head">
        <h3>{t("vocabFromThisLesson")}</h3>
        <p>{t("vocabSelectHint")}</p>
      </div>

      {selectedText ? (
        <div className="vocab-inline-save">
          <span>
            {t("readingSelected")} <strong>“{selectedText}”</strong>
          </span>
          <div className="vocab-inline-actions">
            <button
              type="button"
              className="vocab-btn vocab-btn--primary"
              disabled={saving}
              onClick={() => save({ word: selectedText })}
            >
              {saving ? t("vocabSaving") : t("vocabSaveWord")}
            </button>
            {onClearSelection && (
              <button type="button" className="vocab-btn" onClick={onClearSelection}>
                {t("readingDismiss")}
              </button>
            )}
          </div>
        </div>
      ) : null}

      {openSuggestions.length > 0 && (
        <ul className="vocab-inline-suggestions">
          {openSuggestions.map((item) => (
            <li key={item.word}>
              <div>
                <strong>{item.word}</strong>
                {item.meaning ? <p>{item.meaning}</p> : null}
              </div>
              <button
                type="button"
                className="vocab-btn vocab-btn--primary"
                disabled={saving}
                onClick={() => save({ word: item.word, meaning: item.meaning })}
              >
                {t("vocabAdd")}
              </button>
            </li>
          ))}
        </ul>
      )}

      {words.length > 0 && (
        <ul className="vocab-inline-chips">
          {words.map((item) => (
            <li key={item.id}>{item.display_word || item.word}</li>
          ))}
        </ul>
      )}

      {notice ? <p className="vocab-inline-ok">{notice}</p> : null}
      {error ? <p className="vocab-inline-error">{error}</p> : null}
    </section>
  )
}
